import type { MessageFilters, FilterField, FieldFilter } from '../hooks/useMessageFilters';
import type {
  PersistedFilter,
  GroupableSelection,
  PersistedFilterField,
  PersistedFieldFilter
} from '../../api/server/alerts';
import type { Group } from '../../api/server/groups';
import { filtersToExploreFilter } from '../hooks/filtersToExploreFilter';
import type { ExploreFilter } from '../../api/server/explore';

// NOTE: Group emission is best-effort. A group whose entire CURRENT membership is
// selected becomes { kind:'group' }; otherwise members stay { kind:'value' }. This
// relaxes spec #227's "all-members-individually != whole-group" distinction.
//
// NOTE: An exclude-mode constraint on the GROUPABLE field is not represented in
// `groupable` (which is include-semantics) — it is intentionally dropped. The
// groupable selection models which groups/values are active, not exclusions.

// FilterField (camelCase) -> Group.criteria key (snake_case). Inverse of
// UNGROUPED_TO_FILTER_FIELD in useGroupData.ts. Only the 5 groupable-capable
// fields have group criteria; others fall through to their own name.
const FILTER_FIELD_TO_CRITERIA_KEY: Partial<Record<FilterField, string>> = {
  channels: 'channels',
  talkgroupTags: 'talkgroup_tags',
  radioIds: 'radio_ids',
  speakerIds: 'speakers',
  tags: 'tags'
};

function criteriaKeyFor(field: string): string {
  return FILTER_FIELD_TO_CRITERIA_KEY[field as FilterField] ?? field;
}

const ALL_FIELDS: FilterField[] = [
  'channels',
  'sourceTypes',
  'deviceIds',
  'feedIds',
  'talkgroups',
  'talkgroupTags',
  'radioIds',
  'speakerIds',
  'tags'
];

export function emptyMessageFilters(): MessageFilters {
  const empty = (): FieldFilter => ({ values: [], mode: 'include' });
  return {
    channels: empty(),
    sourceTypes: empty(),
    deviceIds: empty(),
    feedIds: empty(),
    talkgroups: empty(),
    talkgroupTags: empty(),
    radioIds: empty(),
    speakerIds: empty(),
    tags: empty(),
    emergency: false
  };
}

function groupMembers(group: Group, criteriaKey: string): string[] {
  const c = group.criteria as Record<string, string[] | undefined>;
  return c?.[criteriaKey] ?? [];
}

export function emitGroupable(
  selectedIds: string[] | null,
  groups: Group[],
  groupableField: string
): GroupableSelection {
  if (selectedIds === null) return { all: true };
  const criteriaKey = criteriaKeyFor(groupableField);
  const remaining = new Set(selectedIds.filter((v) => v !== '__empty_selection__'));
  const selections: Array<{ kind: 'group' | 'value'; id: string }> = [];
  for (const g of groups) {
    const members = groupMembers(g, criteriaKey);
    if (members.length > 0 && members.every((m) => remaining.has(m))) {
      selections.push({ kind: 'group', id: g.group_id });
      for (const m of members) remaining.delete(m);
    }
  }
  for (const id of remaining) selections.push({ kind: 'value', id });
  return { selections };
}

export function resolveGroupableToSelectedIds(
  groupable: GroupableSelection,
  groups: Group[],
  groupableField: string
): string[] | null {
  if ('all' in groupable) return null;
  const criteriaKey = criteriaKeyFor(groupableField);
  const byId = new Map(groups.map((g) => [g.group_id, g]));
  const out = new Set<string>();
  for (const sel of groupable.selections) {
    if (sel.kind === 'value') out.add(sel.id);
    else {
      const g = byId.get(sel.id);
      if (g) for (const m of groupMembers(g, criteriaKey)) out.add(m);
    }
  }
  return Array.from(out);
}

export interface ToPersistedOpts {
  groupableField: FilterField;
  selectedGroupIds: string[] | null;
  keyword: string;
  groups: Group[];
}

export function messageFiltersToPersisted(
  filters: MessageFilters,
  opts: ToPersistedOpts
): PersistedFilter {
  const fields: Partial<Record<PersistedFilterField, PersistedFieldFilter>> = {};
  for (const f of ALL_FIELDS) {
    if (f === opts.groupableField) continue;
    const ff = filters[f];
    if (ff.values.length > 0) fields[f] = { values: [...ff.values], mode: ff.mode };
  }
  const persisted: PersistedFilter = {
    fields,
    groupable: emitGroupable(opts.selectedGroupIds, opts.groups, opts.groupableField)
  };
  if (opts.keyword.trim() !== '') persisted.keyword = opts.keyword;
  if (filters.emergency) persisted.emergency = true;
  return persisted;
}

export interface FromPersistedOpts {
  groupableField: FilterField;
  groups: Group[];
}

export function persistedToMessageFilters(
  persisted: PersistedFilter,
  opts: FromPersistedOpts
): { filters: MessageFilters; selectedGroupIds: string[] | null; keyword: string } {
  const filters = emptyMessageFilters();
  for (const [field, ff] of Object.entries(persisted.fields)) {
    if (ff) filters[field as FilterField] = { values: [...ff.values], mode: ff.mode };
  }
  filters.emergency = !!persisted.emergency;
  const selectedGroupIds = resolveGroupableToSelectedIds(
    persisted.groupable,
    opts.groups,
    opts.groupableField
  );
  if (selectedGroupIds) {
    filters[opts.groupableField] = { values: selectedGroupIds, mode: 'include' };
  }
  return { filters, selectedGroupIds, keyword: persisted.keyword ?? '' };
}

export interface ToExploreOpts {
  orgId: string;
  groupableField: FilterField;
  groups: Group[];
  start?: string;
  end?: string;
}

export function persistedToExploreFilter(
  persisted: PersistedFilter,
  opts: ToExploreOpts
): ExploreFilter {
  const { filters, keyword } = persistedToMessageFilters(persisted, {
    groupableField: opts.groupableField,
    groups: opts.groups
  });
  // filtersToExploreFilter requires start/end, but the explore snapshot API treats
  // them as optional. When a caller omits the range, omit the keys entirely rather
  // than emitting empty strings (which the backend would reject as invalid dates).
  const explore = filtersToExploreFilter(filters, {
    orgId: opts.orgId,
    start: opts.start ?? '',
    end: opts.end ?? '',
    keyword
  });
  if (opts.start === undefined) delete explore.start;
  if (opts.end === undefined) delete explore.end;
  return explore;
}
